import { memo, type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type FormField } from '@air/api/types';
import { BoardAddIcon, CloudUploadIcon, SendIcon, SquarePenIcon } from '@air/design-icons';
import { useShortUrlListCustomFields } from '@air/feature-custom-fields/hooks';
import { Spinner } from '@air/primitive-spinner';
import { useShortIdContext } from '@air/provider-short-id';

import { contentCollectionFormSelector } from '../redux';

export const ContentCollectionSidebarInstructions = memo(() => {
  const form = useSelector(contentCollectionFormSelector);
  const { shortId } = useShortIdContext();
  const { data: shortUrlCustomFields, isLoading: isLoadingShortUrlCustomFields } = useShortUrlListCustomFields({
    shortId: shortId,
  });

  const instructionItems = useMemo<{ description: ReactNode; icon: ReactNode; label: string }[] | null>(() => {
    if (form.autoSubmit) {
      return [
        {
          description: `Your files will be uploaded and shared with ${form.workspace.name} instantly. Once a file has been uploaded, you won't be able to modify or remove it.`,
          icon: <CloudUploadIcon size="sm" />,
          label: 'Drop your files',
        },
      ];
    }

    const customFields = form.fields.reduce<(FormField & { name?: string })[]>((customFields, field) => {
      if (field.type === 'custom_field') {
        customFields.push({
          ...field,
          name: shortUrlCustomFields?.data.find(({ id }) => id === field.customFieldId)?.name,
        });
      }

      return customFields;
    }, []);
    const requiredCustomFields = customFields.filter((field) => field.required);
    const isDescriptionRequired = form.fields.some((field) => field.type === 'description' && field.required);

    // If there are any required custom fields, we need to wait for data to load before being able to render their names
    if (requiredCustomFields.length && isLoadingShortUrlCustomFields) {
      return null;
    }

    let metadataItemDescription: ReactNode = 'You can add titles and descriptions for each file.';

    if (!requiredCustomFields.length && isDescriptionRequired) {
      metadataItemDescription = 'Provide the required fields for each file.';
    } else if (!requiredCustomFields.length && !isDescriptionRequired) {
      metadataItemDescription = 'Provide the optional fields for all files or set them individually.';
    } else {
      const requiredFieldNames = requiredCustomFields.map((field) => field.name?.trim()).filter(Boolean);

      if (isDescriptionRequired) {
        requiredFieldNames.unshift('Description');
      }
      metadataItemDescription = (
        <>
          Provide the required fields for all files or set them individually.
          <br />
          <span className="inline-block pt-1 font-semibold">Required fields:</span> {requiredFieldNames.join(', ')}
        </>
      );
    }

    return [
      {
        description: 'Choose the files you want to submit.',
        icon: <BoardAddIcon size="sm" />,
        label: 'Add your files',
      },
      {
        description: metadataItemDescription,
        icon: <SquarePenIcon size="sm" />,
        label: 'Fill in details',
      },
      {
        description: `${form.workspace.name} will receive your files and be notified after you press submit.`,
        icon: <SendIcon size="sm" />,
        label: 'Send your files',
      },
    ];
  }, [form, shortUrlCustomFields, isLoadingShortUrlCustomFields]);

  // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
  if (!instructionItems) return <Spinner className="mx-auto" />;

  return (
    <div className="rounded-8 border ads:border-default border-gray-4 ads:bg-raised bg-gray-2 p-4">
      {instructionItems.map(({ icon, label, description }) => (
        <div
          className="flex gap-2 border-b ads:border-b-default border-b-gray-4 py-3 first:pt-0 last:border-b-0 last:pb-0"
          key={label}
        >
          <div className="flex size-6 flex-shrink-0 items-center justify-center p-1">{icon}</div>
          <div className="flex flex-col gap-0.5">
            <h2 className="font-medium ads:text-primary text-14 text-gray-12">{label}</h2>
            <p className="ads:text-metadata text-14 text-gray-10">{description}</p>
          </div>
        </div>
      ))}
    </div>
  );
});

ContentCollectionSidebarInstructions.displayName = 'ContentCollectionSidebarInstructions';
